import NoDataIcon from "@assets/icons/new/no_data.svg?react";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { CardSelectorSkeleton } from "@components/common/skeleton-loading/card-selector-skeleton";
import { SphereDivider } from "@components/common/sphere-divider";
import { ReactSetStateFunction } from "@custom-types/types";
import { CardSelector, Text, CardItemDetails, neutral } from "@flat-ui";
import { useStripeProducts } from "@hooks/use-stripe-products";
import { Grid2, Stack } from "@mui/material";
import { CreditFeatureItem } from "@pages/settings/subscription-settings/credits/credit-feature-item";
import { CreditPurchase } from "@pages/settings/subscription-settings/credits/credit-purchase";
import { RequestCreditQuote } from "@pages/settings/subscription-settings/credits/request-credit-quote";
import {
  CREDIT_DISPLAY_NAME,
  CREDITS_DISPLAY_NAME,
} from "@src/constants/credit-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { fetchingSdbCompanyFlagsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  convertStripeProductToPackageCardItem,
  formatStripeDisplayPrice,
} from "@utils/stripe-product-utils";

/** List of credit-related features */
const CREDIT_FEATURES = [
  `Use ${CREDITS_DISPLAY_NAME} to generate floorplans`,
  `Purchase ${CREDITS_DISPLAY_NAME} tailored to your needs`,
  `Use ${CREDITS_DISPLAY_NAME} within 12 months`,
];

interface Props {
  /** The currently selected credit package */
  selectedPackage?: FaroCoreApiTypes.StripeProduct;

  /** Callback function to set the selected credit package */
  setSelectedPackage: ReactSetStateFunction<
    FaroCoreApiTypes.StripeProduct | undefined
  >;
}

/** Render the credit packages list for purchasing credits. */
export function CreditPackages({
  selectedPackage,
  setSelectedPackage,
}: Props): JSX.Element {
  const { fetchSelectedCompanyContext } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );
  const { stripeProducts, isLoading } = useStripeProducts();
  const cardItems = convertStripeProductToPackageCardItem(stripeProducts);

  if (cardItems.length === 0 && !isLoading) {
    return (
      <EmptyPage
        title={`No ${CREDIT_DISPLAY_NAME} packages available`}
        subtitle="Please check back later or contact support for assistance."
        icon={NoDataIcon}
      />
    );
  }

  function handleOnSelect(productId: CardItemDetails["value"]): void {
    const selectedProduct = stripeProducts.find(
      (product) => product.id === productId
    );

    if (selectedProduct) {
      setSelectedPackage(selectedProduct);
    }
  }

  // TODO: Remove this reduced price https://faro01.atlassian.net/browse/ST-3530
  const reducedPrice = selectedPackage
    ? (parseFloat(selectedPackage.price.price) / 2).toString()
    : "";

  return (
    <>
      <Stack
        sx={{
          alignItems: "center",
          marginTop: {
            sm: "0px",
            md: "50px",
          },
          marginBottom: "50px",
        }}
      >
        <Text variant="heading24"> Add {CREDITS_DISPLAY_NAME}</Text>
        <Text variant="bodyM">
          Select the {CREDIT_DISPLAY_NAME} package that best fits your needs.
        </Text>
      </Stack>

      <Grid2 container sx={{ marginX: "auto", width: { lg: "70%" } }}>
        <Grid2
          component="div"
          size={{
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6,
            xl: 6,
          }}
        >
          <Stack>
            {isLoading || fetchSelectedCompanyContext === "pending" ? (
              <CardSelectorSkeleton />
            ) : (
              <CardSelector
                cardDirection="column"
                defaultBorderColor={neutral[200]}
                defaultBackgroundColor={neutral[0]}
                selectedBackgroundColor={neutral[50]}
                cardItems={cardItems}
                selectedValue={selectedPackage?.id}
                onSelect={handleOnSelect}
              />
            )}
          </Stack>
        </Grid2>

        <Grid2
          component="div"
          size={{
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6,
            xl: 6,
          }}
        >
          <Stack
            sx={{
              backgroundColor: neutral[50],
              borderRadius: "4px",
              marginTop: { xs: "15px", sm: "0px" },
              marginLeft: { xs: "0px", sm: "15px" },
              padding: "32px",
            }}
          >
            <Text variant="heading16">Check out</Text>

            <SphereDivider sx={{ marginY: "12px" }} />

            {CREDIT_FEATURES.map((text, index) => (
              <CreditFeatureItem key={index} featureText={text} />
            ))}

            <SphereDivider sx={{ marginY: "12px" }} />

            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "24px",
              }}
            >
              <Text variant="bodyM">Total</Text>
              {selectedPackage?.price.price && (
                <Text variant="bodyM">
                  {formatStripeDisplayPrice(reducedPrice)}
                </Text>
              )}
            </Stack>

            <CreditPurchase selectedPackage={selectedPackage} />
            <RequestCreditQuote selectedPackage={selectedPackage} />
          </Stack>
        </Grid2>
      </Grid2>
    </>
  );
}
